import React, { useState, useEffect, useCallback } from 'react';
import { Space, Tag, Typography, Button, Tooltip, Spin, Badge, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UpOutlined,
  DownOutlined,
  HistoryOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { EXECUTOR_API } from '../types';
import { buildAuthHeaders } from '../utils/userStore';
import ExecutionDetailDrawer, { ExecutionDetail } from './ExecutionDetailDrawer';

const { Text } = Typography;

interface Execution {
  run_id: string;
  status: 'success' | 'error';
  started_at: string;
  finished_at: string;
  duration_ms: number;
}

interface Props {
  agentId: string | null;
  onViewAll: () => void;
}

const PANEL_HEIGHT = 240;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

const ExecutionPanel: React.FC<Props> = ({ agentId, onViewAll }) => {
  const [open, setOpen] = useState(false);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ExecutionDetail | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetch5 = useCallback(async (showLoading = false) => {
    if (!agentId) return;
    if (showLoading) setLoading(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/${agentId}/executions?limit=5&offset=0`, {
        headers: buildAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const incoming: Execution[] = data.executions || [];
        setExecutions(prev => {
          const firstId = prev[0]?.run_id;
          return incoming[0]?.run_id !== firstId ? incoming : prev;
        });
      }
    } catch {
      // silent
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [agentId]);

  const fetchDetail = useCallback(async (runId: string) => {
    if (!agentId) return;
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/${agentId}/executions/${runId}`, {
        headers: buildAuthHeaders(),
      });
      if (res.ok) {
        setDetail(await res.json());
        setDrawerOpen(true);
      } else {
        message.error('No se pudo cargar los detalles');
      }
    } catch {
      message.error('Error al cargar detalles');
    }
  }, [agentId]);

  useEffect(() => { fetch5(); }, [fetch5]);

  useEffect(() => {
    const id = setInterval(() => fetch5(), 5000);
    return () => clearInterval(id);
  }, [fetch5]);

  const last = executions[0];

  return (
    <>
      <div style={{
        borderTop: '1px solid #f0f0f0',
        background: '#fff',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'height 0.2s ease',
        height: open ? PANEL_HEIGHT : 40,
        overflow: 'hidden',
      }}>
        {/* Header bar — always visible */}
        <div
          style={{
            height: 40,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            gap: 10,
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setOpen(v => !v)}
        >
          <Space size={6} align="center">
            <HistoryOutlined style={{ fontSize: 13, color: '#595959' }} />
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>Ejecuciones</Text>
            {executions.length > 0 && (
              <Badge count={executions.length} color="#1677ff" style={{ boxShadow: 'none' }} />
            )}
          </Space>

          {/* Last execution preview */}
          {last && !open && (
            <Space size={6} style={{ marginLeft: 8 }}>
              <Tag
                icon={last.status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                color={last.status === 'success' ? 'green' : 'red'}
                style={{ margin: 0, fontSize: 11 }}
              >
                {last.status === 'success' ? 'OK' : 'Error'}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {(last.duration_ms / 1000).toFixed(2)}s · hace {timeAgo(last.started_at)}
              </Text>
            </Space>
          )}

          {/* Right side */}
          <Space size={8} style={{ marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
            <Tooltip title="Actualizar">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => fetch5(true)}
                style={{ padding: '0 4px', height: 24 }}
              />
            </Tooltip>
            <Tooltip title="Ver historial completo">
              <Button
                type="text"
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={onViewAll}
                style={{ padding: '0 4px', height: 24, fontSize: 12 }}
              >
                Ver todas
              </Button>
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={open ? <DownOutlined /> : <UpOutlined />}
              onClick={() => setOpen(v => !v)}
              style={{ padding: '0 4px', height: 24 }}
            />
          </Space>
        </div>

        {/* Body — execution rows */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 8px' }}>
          {loading && executions.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <Spin size="small" />
            </div>
          ) : executions.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '20px 0', textAlign: 'center' }}>
              Sin ejecuciones — las llamadas al webhook aparecerán aquí
            </Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px 4px 0', color: '#8c8c8c', fontWeight: 500, width: 70 }}>Estado</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#8c8c8c', fontWeight: 500, width: 80 }}>Duración</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#8c8c8c', fontWeight: 500 }}>Inicio</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#8c8c8c', fontWeight: 500, width: 70 }}>Run ID</th>
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {executions.map(ex => (
                  <tr key={ex.run_id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '5px 8px 5px 0' }}>
                      <Tag
                        icon={ex.status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        color={ex.status === 'success' ? 'green' : 'red'}
                        style={{ margin: 0, fontSize: 11 }}
                      >
                        {ex.status === 'success' ? 'OK' : 'Error'}
                      </Tag>
                    </td>
                    <td style={{ padding: '5px 8px', color: '#595959' }}>
                      {(ex.duration_ms / 1000).toFixed(2)}s
                    </td>
                    <td style={{ padding: '5px 8px', color: '#8c8c8c' }}>
                      {new Date(ex.started_at).toLocaleTimeString('es-ES')}
                      {' '}
                      <span style={{ fontSize: 11, color: '#bfbfbf' }}>({timeAgo(ex.started_at)})</span>
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      <Tooltip title={ex.run_id}>
                        <Text
                          type="secondary"
                          style={{ fontSize: 11, fontFamily: 'monospace', cursor: 'pointer' }}
                          onClick={() => { navigator.clipboard.writeText(ex.run_id); message.success('run_id copiado'); }}
                        >
                          {ex.run_id.slice(0, 8)}…
                        </Text>
                      </Tooltip>
                    </td>
                    <td style={{ padding: '5px 4px', textAlign: 'right' }}>
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, fontSize: 12, height: 'auto' }}
                        onClick={() => fetchDetail(ex.run_id)}
                      >
                        Detalles
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ExecutionDetailDrawer
        detail={detail}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
      />
    </>
  );
};

export default ExecutionPanel;
